'use client';

import type { BankAccount } from '@/services/PaymentService';

export interface PaymentBankTransferFormValue {
  selectedBank: number | null;
  transferAmount: string;
  transferDate: string;
  senderName: string;
  senderBank: string;
  referenceNo: string;
  senderEmail: string;
  senderPhone: string;
  notes: string;
  receiptFile: File | null;
}

export interface PaymentBankTransferFormProps {
  value: PaymentBankTransferFormValue;
  onChange: (next: Partial<PaymentBankTransferFormValue>) => void;
  bankAccounts: BankAccount[];
  /** Çeviri: (key, fallback) => string */
  t: (key: string, fallback: string) => string;
  /** Gönder butonu göster ve form submit'i bu callback ile yap (InvoicePayment) */
  onSubmit?: (e: React.FormEvent) => void;
  processing?: boolean;
  uploadingReceipt?: boolean;
  /** Tutar üst sınırı (fatura kalan tutar); aşılırsa validasyon mesajı */
  maxAmount?: number;
  /** Kısmi ödeme minimum tutarı */
  minAmount?: number;
  /** Sadece bilgi/ön doldurma (ReservationBookNow – submit yok) */
  prefillOnly?: boolean;
  /** prefillOnly时 gösterilecek açıklama */
  prefillNote?: string;
}

const inputClass = 'border border-[rgba(0,0,0,0.08)] focus:!border-[rgba(0,0,0,0.40)] transition-all ease-in-out duration-300 rounded-lg text-[#4F545A] font-medium text-sm sm:text-[15px] px-4 sm:px-6 py-3 sm:py-4 disabled:opacity-50 disabled:cursor-not-allowed w-full';

export default function PaymentBankTransferForm({
  value,
  onChange,
  bankAccounts,
  t,
  onSubmit,
  processing = false,
  uploadingReceipt = false,
  maxAmount,
  minAmount,
  prefillOnly = false,
  prefillNote,
}: PaymentBankTransferFormProps) {
  const {
    selectedBank,
    transferAmount,
    transferDate,
    senderName,
    senderBank,
    referenceNo,
    senderEmail,
    senderPhone,
    notes,
    receiptFile,
  } = value;

  const content = (
    <>
      <div>
        <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.select_bank', 'Bank for Transfer')}</label>
        {bankAccounts.length === 0 ? (
          <p className="text-sm text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            {t('payment.no_bank_accounts', 'No bank accounts are configured. Please contact us or pay by card.')}
          </p>
        ) : null}
        <div className="grid grid-cols-1 gap-3">
          {bankAccounts.map((bank) => (
            <div
              key={bank.id}
              role="button"
              tabIndex={0}
              onClick={() => onChange({ selectedBank: bank.id })}
              onKeyDown={(e) => e.key === 'Enter' && onChange({ selectedBank: bank.id })}
              className={`p-4 border rounded-lg cursor-pointer transition ${selectedBank === bank.id ? 'border-blue-500 bg-blue-50 border-alsero-first bg-alsero-first/5' : 'hover:border-gray-400'}`}
            >
              <div className="font-medium">{bank.bank_name}</div>
              <div className="text-sm text-gray-600">{bank.account_name}</div>
              <div className="text-sm font-mono mt-1">{bank.iban}</div>
              {bank.swift_code && <div className="text-gray-500 text-xs mt-1">SWIFT: {bank.swift_code}</div>}
              {bank.currency_code && <span className="inline-block mt-1 text-xs font-medium text-gray-600">{bank.currency_code}</span>}
            </div>
          ))}
        </div>
        {bankAccounts.length > 0 && selectedBank == null && (
          <p className="mt-2 text-sm font-medium text-amber-800" role="status">
            {t('payment.select_bank_required', 'Please select which bank account you will transfer to.')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.amount', 'Transfer Amount')}</label>
          <input
            type="number"
            value={transferAmount}
            onChange={(e) => onChange({ transferAmount: e.target.value })}
            className={inputClass}
            step="0.01"
            required={!prefillOnly}
          />
          {maxAmount != null && transferAmount && parseFloat(transferAmount) > maxAmount && (
            <p className="text-xs text-red-600 mt-1">{t('payment.error.amount_exceeds', `Amount cannot exceed ${maxAmount.toFixed(2)}`)}</p>
          )}
          {minAmount != null && transferAmount && parseFloat(transferAmount) + 0.001 < minAmount && (
            <p className="text-xs text-red-600 mt-1">{t('payment.error.amount_below_min', `Minimum amount is ${minAmount.toFixed(2)}`)}</p>
          )}
        </div>
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.transfer_date', 'Transfer Date')}</label>
          <input
            type="date"
            value={transferDate}
            onChange={(e) => onChange({ transferDate: e.target.value })}
            className={inputClass}
            max={new Date().toISOString().split('T')[0]}
            required={!prefillOnly}
          />
        </div>
      </div>

      <div>
        <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.sender_name', 'Sender Name Surname')}</label>
        <input type="text" value={senderName} onChange={(e) => onChange({ senderName: e.target.value })} className={inputClass} required={!prefillOnly} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.sender_bank', 'Sender Bank')}</label>
          <input type="text" value={senderBank} onChange={(e) => onChange({ senderBank: e.target.value })} className={inputClass} />
        </div>
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.reference_no', 'Reference No')}</label>
          <input type="text" value={referenceNo} onChange={(e) => onChange({ referenceNo: e.target.value })} className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.email', 'Email')}</label>
          <input type="email" value={senderEmail} onChange={(e) => onChange({ senderEmail: e.target.value })} className={inputClass} required={!prefillOnly} />
        </div>
        <div>
          <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.phone', 'Phone')}</label>
          <input type="tel" value={senderPhone} onChange={(e) => onChange({ senderPhone: e.target.value })} className={inputClass} />
        </div>
      </div>

      <div>
        <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.receipt', 'Receipt (Optional)')}</label>
        <input
          type="file"
          accept=".jpg,.jpeg,.png,.pdf"
          onChange={(e) => onChange({ receiptFile: e.target.files?.[0] ?? null })}
          className={inputClass}
        />
        <p className="text-xs text-gray-500 mt-1">{t('payment.receipt_hint', 'JPG, PNG or PDF format, max 5MB')}</p>
      </div>

      <div>
        <label className="block font-semibold text-base sm:text-lg text-alsero-dark-gray mb-2">{t('payment.notes', 'Note (Optional)')}</label>
        <textarea value={notes} onChange={(e) => onChange({ notes: e.target.value })} className={inputClass} rows={2} />
      </div>

      {prefillNote && <p className="text-xs text-gray-500">{prefillNote}</p>}

      {!prefillOnly && onSubmit && (
        <button
          type="submit"
          disabled={processing || uploadingReceipt || !selectedBank}
          className="w-full py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {processing ? t('payment.sending', 'Sending...') : uploadingReceipt ? t('payment.uploading_receipt', 'Uploading receipt...') : t('payment.submit_transfer', 'Notify Transfer')}
        </button>
      )}
    </>
  );

  if (prefillOnly) return <div className="space-y-6">{content}</div>;
  return <form onSubmit={onSubmit} className="space-y-6">{content}</form>;
}
